import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import {
  clearSessionStorage,
  getAccessToken,
  getRefreshToken,
  setTokens,
} from '@/shared/lib/auth/tokenStorage';
import type { TokenResponse } from '@/types/api';

export type ApiClientOptions = {
  baseURL: string;
  appVersion: string;
  appOrigem: string;
  getToken: () => string | null;
  getRefreshToken: () => string | null;
  setTokens: (access: string, refresh: string) => void;
  onUnauthorized: () => void;
  onVersionOutdated: (mensagem: string) => void;
  /** 409 ESCOLA_NAO_SELECIONADA: recoloca a escola e devolve true para repetir a chamada. */
  onEscolaNaoSelecionada?: () => Promise<boolean>;
};

let refreshPromise: Promise<string> | null = null;

class RenovacaoRejeitada extends Error {
  constructor() {
    super('renovacao-rejeitada');
    this.name = 'RenovacaoRejeitada';
  }
}
let escolaPromise: Promise<boolean> | null = null;

type ConfigComRetry = InternalAxiosRequestConfig & {
  _escolaRetry?: boolean;
  _sessaoRetry?: boolean;
};

function codigoErro(error: AxiosError): string | null {
  const data = error.response?.data;
  if (data && typeof data === 'object' && 'codigo' in data) {
    const codigo = (data as { codigo?: unknown }).codigo;
    if (typeof codigo === 'string' && codigo.trim()) return codigo;
  }
  return null;
}

function createRefreshClient(baseURL: string, appVersion: string, appOrigem: string) {
  return axios.create({
    baseURL,
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
      'x-app-version': appVersion,
      'x-app-origem': appOrigem,
    },
  });
}

async function refreshAccessToken(
  baseURL: string,
  appVersion: string,
  appOrigem: string,
  getRefreshTokenFn: () => string | null,
  setTokensFn: (a: string, r: string) => void,
): Promise<string> {
  const refresh = getRefreshTokenFn();
  if (!refresh) throw new RenovacaoRejeitada();

  if (!refreshPromise) {
    const pendente = (async () => {
      try {
        const client = createRefreshClient(baseURL, appVersion, appOrigem);
        const { data } = await client.post<TokenResponse>('/v1/auth/renovar', {
          refreshToken: refresh,
        });
        setTokensFn(data.accessToken, data.refreshToken);
        return data.accessToken;
      } catch (error) {
        const status = axios.isAxiosError(error) ? error.response?.status : undefined;
        if (status === 401 || status === 400 || status === 422) {
          throw new RenovacaoRejeitada();
        }
        throw error;
      }
    })();
    refreshPromise = pendente;
    void pendente.finally(() => {
      if (refreshPromise === pendente) refreshPromise = null;
    });
  }
  return refreshPromise;
}

async function repetirComSessaoNova(
  client: ReturnType<typeof axios.create>,
  original: ConfigComRetry,
  options: ApiClientOptions,
  error: AxiosError,
) {
  try {
    const newToken = await refreshAccessToken(
      options.baseURL,
      options.appVersion,
      options.appOrigem,
      options.getRefreshToken,
      options.setTokens,
    );
    original.headers.Authorization = `Bearer ${newToken}`;
    return client.request(original);
  } catch (falha) {
    if (falha instanceof RenovacaoRejeitada) {
      clearSessionStorage();
      options.onUnauthorized();
    }
    return Promise.reject(error);
  }
}

function mensagemDeErro(error: AxiosError): string {
  const data = error.response?.data;
  if (data && typeof data === 'object' && 'mensagem' in data) {
    const m = (data as { mensagem?: unknown }).mensagem;
    if (typeof m === 'string' && m.trim()) return m;
  }
  return 'Atualize o aplicativo para continuar.';
}

export function createApiClient(options: ApiClientOptions) {
  const client = axios.create({
    baseURL: options.baseURL,
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
      'x-app-version': options.appVersion,
      'x-app-origem': options.appOrigem,
    },
  });

  client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      config.headers.set('Content-Type', false);
    }
    const token = options.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      if (error.response?.status === 426) {
        options.onVersionOutdated(mensagemDeErro(error));
        return Promise.reject(error);
      }

      const original = error.config as ConfigComRetry | undefined;
      if (
        original &&
        !original._escolaRetry &&
        error.response?.status === 409 &&
        codigoErro(error) === 'ESCOLA_NAO_SELECIONADA' &&
        options.onEscolaNaoSelecionada &&
        !(original.url ?? '').includes('/v1/auth/')
      ) {
        if (!escolaPromise) {
          const restaurar = options.onEscolaNaoSelecionada;
          escolaPromise = restaurar().finally(() => {
            escolaPromise = null;
          });
        }
        let restaurada = false;
        try {
          restaurada = await escolaPromise;
        } catch {
          restaurada = false;
        }
        if (restaurada) {
          original._escolaRetry = true;
          return client.request(original);
        }
      }

      if (!original || error.response?.status !== 401) {
        return Promise.reject(error);
      }

      const url = original.url ?? '';
      if (
        url.includes('/v1/auth/login') ||
        url.includes('/v1/auth/renovar') ||
        url.includes('/v1/auth/esqueci-senha') ||
        url.includes('/v1/auth/definir-senha')
      ) {
        return Promise.reject(error);
      }

      if (original._sessaoRetry) {
        clearSessionStorage();
        options.onUnauthorized();
        return Promise.reject(error);
      }
      original._sessaoRetry = true;
      return repetirComSessaoNova(client, original, options, error);
    },
  );

  return client;
}

let unauthorizedHandler: (() => void) | null = null;
let versionOutdatedHandler: ((mensagem: string) => void) | null = null;
let escolaNaoSelecionadaHandler: (() => Promise<boolean>) | null = null;

export function setUnauthorizedHandler(handler: () => void) {
  unauthorizedHandler = handler;
}

export function setVersionOutdatedHandler(handler: (mensagem: string) => void) {
  versionOutdatedHandler = handler;
}

export function setEscolaNaoSelecionadaHandler(handler: () => Promise<boolean>) {
  escolaNaoSelecionadaHandler = handler;
}

export const apiClient = createApiClient({
  baseURL: import.meta.env.VITE_API_URL ?? 'http://localhost:8080',
  appVersion: import.meta.env.VITE_APP_VERSION ?? '0.1.2',
  appOrigem: 'WEB_ESCOLA',
  getToken: getAccessToken,
  getRefreshToken,
  setTokens,
  onUnauthorized: () => {
    unauthorizedHandler?.();
  },
  onVersionOutdated: (mensagem) => {
    versionOutdatedHandler?.(mensagem);
  },
  onEscolaNaoSelecionada: () => escolaNaoSelecionadaHandler?.() ?? Promise.resolve(false),
});

const API_BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8080';
const APP_VERSION = import.meta.env.VITE_APP_VERSION ?? '0.1.2';

/** HTTP respondeu antes de abrir o fluxo (401, 404, 409, 422...). Nada foi processado. */
export class FalhaAntesDoFluxo extends Error {
  readonly status: number;
  constructor(status: number) {
    super(`fluxo-recusado-${status}`);
    this.name = 'FalhaAntesDoFluxo';
    this.status = status;
  }
}

export type EventoDoFluxo = { evento: string; dado: unknown };

function lerEvento(bloco: string): EventoDoFluxo | null {
  let evento = 'message';
  const dados: string[] = [];
  for (const linha of bloco.split('\n')) {
    if (linha.startsWith('event:')) evento = linha.slice(6).trim();
    else if (linha.startsWith('data:')) dados.push(linha.slice(5).replace(/^ /, ''));
  }
  if (dados.length === 0) return null;
  const texto = dados.join('\n');
  try {
    return { evento, dado: JSON.parse(texto) as unknown };
  } catch {
    return { evento, dado: texto };
  }
}

/**
 * POST que responde em text/event-stream (SSE). Axios nao le fluxo no navegador, por isso
 * fetch aqui, com o mesmo token e cabecalhos. Nao renova token: quem chama cai na rota
 * comum quando recebe FalhaAntesDoFluxo.
 */
export async function postEventStream(
  path: string,
  body: unknown,
  onEvento: (evento: EventoDoFluxo) => void,
  signal?: AbortSignal,
): Promise<void> {
  const token = getAccessToken();
  const resposta = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    credentials: 'include',
    signal,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      'x-app-version': APP_VERSION,
      'x-app-origem': 'WEB_ESCOLA',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  const tipo = resposta.headers.get('content-type') ?? '';
  if (!resposta.ok || !tipo.includes('text/event-stream') || !resposta.body) {
    throw new FalhaAntesDoFluxo(resposta.status);
  }
  const leitor = resposta.body.getReader();
  const decodificador = new TextDecoder();
  let pendente = '';
  for (;;) {
    const { done, value } = await leitor.read();
    if (value) pendente += decodificador.decode(value, { stream: true });
    pendente = pendente.replace(/\r\n/g, '\n');
    let fim = pendente.indexOf('\n\n');
    while (fim >= 0) {
      const evento = lerEvento(pendente.slice(0, fim));
      pendente = pendente.slice(fim + 2);
      if (evento) onEvento(evento);
      fim = pendente.indexOf('\n\n');
    }
    if (done) break;
  }
  const resto = lerEvento(pendente.trim());
  if (resto) onEvento(resto);
}
